/**
 * Yard map hooks (#3286)
 *
 * The map is one picture with an optimistic revision (`id` + `generation`)
 * that /parking/current reports. Every write names the revision it was based
 * on; a stale one is a 409 `yard_map_stale` and is never retried here.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { queryKeys } from './queryKeys';
import type { ParkingCurrentStatus, ParkingSpotStatus, YardMapSummary } from './useParking';

export interface YardMapPlacement {
  spotId: string;
  /** Both numbers in [0, 1] or both null (clears the position). */
  mapX: number | null;
  mapY: number | null;
}

interface YardMapWriteResult {
  yardMap: YardMapSummary;
}

/** Same limits staff-api enforces; checked first so the user gets an answer before the upload. */
export const YARD_MAP_ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const YARD_MAP_MAX_BYTES = 8 * 1024 * 1024;

export function validateYardMapFile(file: Pick<File, 'type' | 'size'>): 'type' | 'size' | null {
  if (!(YARD_MAP_ACCEPTED_TYPES as readonly string[]).includes(file.type)) return 'type';
  if (file.size > YARD_MAP_MAX_BYTES) return 'size';
  return null;
}

function inUnitRange(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}

/**
 * The spot's normalized position, or null when it has none. Older servers send
 * no coordinates at all and a half-set or out-of-range pair is not drawn.
 */
export function spotMapPosition(
  spot: Pick<ParkingSpotStatus, 'mapX' | 'mapY'>,
): { x: number; y: number } | null {
  return inUnitRange(spot.mapX) && inUnitRange(spot.mapY) ? { x: spot.mapX, y: spot.mapY } : null;
}

export function isYardMapStaleError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 409 &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'code' in error.data &&
    error.data.code === 'yard_map_stale'
  );
}

/** The quoted `"<mapId>:<generation>"` validator staff-api expects in If-Match. */
export function yardMapRevisionEtag(map: Pick<YardMapSummary, 'id' | 'generation'>): string {
  return `"${map.id}:${String(map.generation)}"`;
}

/** Tenant + id + generation: another tenant's or an older revision's image can never be reused. */
const yardMapImageKey = (tenantId: string | null, mapId: string, generation: number) =>
  ['parkingYardMapImage', tenantId, mapId, generation] as const;

export type YardMapImageStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Authenticated fetch of the picture for exactly the revision /current reported
 * (the server answers 409 if it moved on), exposed as an object URL that is
 * revoked when the revision changes, the query is cancelled or the caller unmounts.
 */
export function useYardMapImage(map: YardMapSummary | null | undefined): {
  url: string | null;
  status: YardMapImageStatus;
  refetch: () => void;
} {
  const queryClient = useQueryClient();
  const tenantId = useAuthStore((s) => s.user?.tenantId ?? null);
  const enabled = map != null && tenantId !== null;

  const query = useQuery({
    queryKey: yardMapImageKey(tenantId, map?.id ?? '', map?.generation ?? 0),
    queryFn: () => {
      if (!map) throw new Error('No yard map');
      return api.getBlob(
        '/parking/yard-map/image?mapId=' +
          encodeURIComponent(map.id) +
          '&generation=' +
          String(map.generation),
      );
    },
    enabled,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !(error instanceof ApiError) && failureCount < 1,
  });

  // The revision moved on between /current and the image read: refresh /current
  // and let the caller fall back to the list; the old coordinates are never
  // drawn on the new picture.
  useEffect(() => {
    if (query.error && isYardMapStaleError(query.error)) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.parking.current() });
    }
  }, [query.error, queryClient]);

  const blob = enabled ? query.data : undefined;
  const [created, setCreated] = useState<{ blob: Blob; url: string } | null>(null);
  useEffect(() => {
    if (!blob) {
      setCreated(null);
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setCreated({ blob, url });
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  // `created` lags one render behind a revision change; never hand out a URL
  // that belongs to another blob (it is revoked by the effect cleanup).
  const url = blob && created?.blob === blob ? created.url : null;
  let status: YardMapImageStatus;
  if (!enabled) status = 'idle';
  else if (query.isError) status = 'error';
  else if (url) status = 'ready';
  else status = 'loading';
  return {
    url,
    status,
    refetch: () => {
      void query.refetch();
    },
  };
}

/** The tenant a write was started for; read when the mutation starts, compared when it answers. */
interface WriteScope {
  tenantId: string | null;
}

const currentWriteScope = (): WriteScope => ({
  tenantId: useAuthStore.getState().user?.tenantId ?? null,
});

/**
 * Apply a write's known outcome to the cached /current, then refetch it. The
 * cache key is not tenant-scoped, so a write that answers after the user moved
 * to another tenant must not touch (or invalidate) that tenant's /current.
 */
function applyToCurrent(
  queryClient: QueryClient,
  scope: WriteScope | undefined,
  update: (current: ParkingCurrentStatus) => ParkingCurrentStatus,
): void {
  const tenantId = useAuthStore.getState().user?.tenantId ?? null;
  if (scope === undefined || scope.tenantId === null || scope.tenantId !== tenantId) return;
  queryClient.setQueryData<ParkingCurrentStatus>(queryKeys.parking.current(), (current) =>
    current ? update(current) : current,
  );
  void queryClient.invalidateQueries({ queryKey: queryKeys.parking.current() });
}

function mapSpots(
  current: ParkingCurrentStatus,
  fn: (spot: ParkingSpotStatus) => ParkingSpotStatus,
): ParkingCurrentStatus['zones'] {
  return current.zones.map((zone) => ({ ...zone, spots: zone.spots.map(fn) }));
}

/** First upload (`If-None-Match: *`) or replace (`If-Match` revision); replace clears every placement. */
export function useUploadYardMapImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, map }: { file: File; map: YardMapSummary | null }) =>
      api.postBlob<YardMapWriteResult>('/parking/yard-map/image', file, {
        headers: map ? { 'If-Match': yardMapRevisionEtag(map) } : { 'If-None-Match': '*' },
      }),
    onMutate: currentWriteScope,
    onSuccess: ({ yardMap }, _variables, scope) => {
      applyToCurrent(queryClient, scope, (current) => ({
        ...current,
        yardMap,
        zones: mapSpots(current, (spot) => ({ ...spot, mapX: null, mapY: null })),
      }));
    },
  });
}

export function useDeleteYardMap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ map }: { map: YardMapSummary }) =>
      api.delete<{ success: boolean }>('/parking/yard-map/image', {
        headers: { 'If-Match': yardMapRevisionEtag(map) },
      }),
    onMutate: currentWriteScope,
    onSuccess: (_result, _variables, scope) => {
      applyToCurrent(queryClient, scope, (current) => ({
        ...current,
        yardMap: null,
        zones: mapSpots(current, (spot) => ({ ...spot, mapX: null, mapY: null })),
      }));
    },
  });
}

export function useSaveYardMapPlacements() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ map, placements }: { map: YardMapSummary; placements: YardMapPlacement[] }) =>
      api.put<YardMapWriteResult & { updated: number }>('/parking/yard-map/placements', {
        expectedMapId: map.id,
        expectedGeneration: map.generation,
        placements,
      }),
    onMutate: currentWriteScope,
    onSuccess: ({ yardMap }, { placements }, scope) => {
      const bySpot = new Map(placements.map((p) => [p.spotId, p]));
      applyToCurrent(queryClient, scope, (current) => ({
        ...current,
        yardMap,
        zones: mapSpots(current, (spot) => {
          const placed = bySpot.get(spot.spotId);
          return placed ? { ...spot, mapX: placed.mapX, mapY: placed.mapY } : spot;
        }),
      }));
    },
  });
}
